/**
 * SuperSplat's frame pipeline for the viewer: the camera renders the world
 * into an RGBA16F target (so the thousands of partial-alpha splat samples per
 * pixel blend without 8-bit rounding — that rounding is the mottle on flat
 * surfaces), then one nearest blit puts it on the backbuffer. No compose, no
 * tonemap pass: the splat shader writes display-ready values, exactly as the
 * editor's camera.ts does. Replaces pc.CameraFrame (whose compose lifted
 * brightness ~9 % and softened the image).
 */

import {
    ADDRESS_CLAMP_TO_EDGE,
    FILTER_NEAREST,
    LAYERID_IMMEDIATE,
    LAYERID_SKYBOX,
    LAYERID_WORLD,
    PIXELFORMAT_DEPTH,
    PIXELFORMAT_RGBA16F,
    type AppBase,
    type CameraComponent,
    Color,
    type Entity,
    RenderPass,
    RenderPassForward,
    RenderTarget,
    Texture
} from 'playcanvas';

import { fragmentGLSL, fragmentShader, vertexGLSL, vertexShader } from './blit-shader';
import { ShaderQuad, SimpleRenderPass } from './simple-render-pass';

class EditorPipeline {
    private readonly app: AppBase;
    private readonly camera: CameraComponent;
    private readonly clearPass: RenderPass;
    private readonly mainPass: RenderPassForward;
    private readonly finalPass: SimpleRenderPass;
    private readonly quad: ShaderQuad;
    mainTarget: RenderTarget | null = null;
    private readonly onResize: () => void;

    constructor(app: AppBase, cameraEntity: Entity) {
        this.app = app;
        this.camera = cameraEntity.camera!;
        const device = app.graphicsDevice;
        const scene = app.scene;

        this.clearPass = new RenderPass(device);
        this.mainPass = new RenderPassForward(device, scene.layers, scene, app.renderer);
        this.quad = new ShaderQuad(device, vertexShader, fragmentShader, 'viewer-final-blit', vertexGLSL, fragmentGLSL);
        this.finalPass = new SimpleRenderPass(device, this.quad, {
            vars: () => {
                const gd = this.app.graphicsDevice;
                const mt = this.mainTarget!;
                return {
                    srcTexture: mt.colorBuffer,
                    blitScale: [mt.width / gd.width, mt.height / gd.height],
                    quadResolve: 0
                };
            }
        });

        this.rebuild();
        this.onResize = () => this.rebuild();
        device.on('resizecanvas', this.onResize);
    }

    /** The 16F colour target, for the paint probe and captures (display-encoded values). */
    get sceneTarget() {
        const mt = this.mainTarget;
        return mt ? { texture: mt.colorBuffer, rt: mt } : null;
    }

    rebuild() {
        const device = this.app.graphicsDevice;
        const width = Math.max(1, device.width);
        const height = Math.max(1, device.height);
        const { mainTarget } = this;
        if (mainTarget && mainTarget.width === width && mainTarget.height === height) {
            return;
        }
        if (mainTarget) {
            mainTarget.resize(width, height);
            return;
        }

        const createTexture = (name: string, format: number) => new Texture(device, {
            name,
            width,
            height,
            format,
            mipmaps: false,
            minFilter: FILTER_NEAREST,
            magFilter: FILTER_NEAREST,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        const colorBuffer = createTexture('viewerColor', PIXELFORMAT_RGBA16F);
        const depthBuffer = createTexture('viewerDepth', PIXELFORMAT_DEPTH);
        this.mainTarget = new RenderTarget({ colorBuffer, depthBuffer, autoResolve: false });

        const layers = this.app.scene.layers;
        const world = layers.getLayerById(LAYERID_WORLD);
        const immediate = layers.getLayerById(LAYERID_IMMEDIATE);
        const skybox = layers.getLayerById(LAYERID_SKYBOX);

        this.clearPass.init(this.mainTarget);
        const c = this.camera.clearColor;
        this.clearPass.setClearColor(new Color(c.r, c.g, c.b, c.a));
        this.clearPass.setClearDepth(1);
        this.clearPass.setClearStencil(0);

        this.mainPass.init(this.mainTarget);
        // the sky first (HDRI scenes with showSkybox), then the world, then overlays
        if (skybox) {
            this.mainPass.addLayer(this.camera, skybox, false, false);
        }
        if (world) {
            this.mainPass.addLayer(this.camera, world, false, false);
            this.mainPass.addLayer(this.camera, world, true, false);
        }
        if (immediate) {
            this.mainPass.addLayer(this.camera, immediate, false, false);
            this.mainPass.addLayer(this.camera, immediate, true, false);
        }

        this.finalPass.init(null);
        this.camera.framePasses = [this.clearPass, this.mainPass, this.finalPass];
    }

    /** Render the camera the engine's default way (VR: one pass per eye). */
    suspend() {
        (this.camera as unknown as { framePasses: unknown }).framePasses = null;
    }

    resume() {
        this.camera.framePasses = [this.clearPass, this.mainPass, this.finalPass];
    }

    destroy() {
        this.app.graphicsDevice.off('resizecanvas', this.onResize);
        (this.camera as unknown as { framePasses: unknown }).framePasses = null;
        this.clearPass.destroy();
        this.mainPass.destroy();
        this.finalPass.destroy();
        this.quad.destroy();
        this.mainTarget?.colorBuffer?.destroy();
        this.mainTarget?.depthBuffer?.destroy();
        this.mainTarget?.destroy();
        this.mainTarget = null;
    }
}

export { EditorPipeline };
